import {
    Dispatch,
    FC,
    PropsWithChildren,
    SetStateAction,
    useState,
} from "react";
import { Modal } from "antd";
import { Box, VStack, Textarea, Input } from "@chakra-ui/react";
import { useAuthenticatedRequestCreator } from "hooks/useRequests";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";

export interface HelpExtraData {
    ebayItemId?: string | null;
    sku?: string | null;
    packageId?: string | null;
    packageType?: string | null;
}

export const BackendHelpExtraDataMap: Record<keyof HelpExtraData, number> = {
    ebayItemId: 1,
    sku: 2,
    packageId: 3,
    packageType: 4,
};

interface HelpModalProps extends HelpExtraData {
    title: string;
    subject: string;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    isModalOpen: boolean;
}

const HelpModal: FC<PropsWithChildren<HelpModalProps>> = ({
    title,
    subject,
    setIsModalOpen,
    isModalOpen,
    children,
    ...extraData
}) => {
    const [supportMessage, setSupportMessage] = useState("");
    const [successfulSubmission, setSuccessfulSubmission] = useState(false);
    const queryClient = useQueryClient();
    const newSupportMessage = useAuthenticatedRequestCreator<void, any>(
        (supportMessage) => ({
            method: "post",
            url: "/support/ticket",
            data: { ...supportMessage },
        }),
    );

    const handleOk = () => {
        const supportMessageData = {
            subject,
            body: supportMessage,
            type: "question",
            extra: toBackendExtraObject(extraData),
            tags: ["website", "submission"],
        };
        submitRequest(supportMessageData);
    };

    const handleCancel = () => {
        setSupportMessage("");
        setIsModalOpen(false);
    };

    const {
        mutateAsync: submitRequest,
        isLoading: isSubmitting,
        isError: submissionError,
    } = useMutation({
        mutationKey: ["support", "new-message"],
        mutationFn: newSupportMessage,
        onSuccess: (data) => {
            setSupportMessage("");
            queryClient.invalidateQueries({ queryKey: ["ticket", "list"] });
            setSuccessfulSubmission(true);
        },
    });

    const toBackendExtraObject = (extra: HelpExtraData) => {
        if (Object.keys(extra).every((k) => !extra[k as keyof HelpExtraData])) {
            // no extra data, no extra object
            return null;
        }

        let result = {};
        Object.keys(extra).forEach((k) => {
            const key = k as keyof HelpExtraData;
            const backendKey = BackendHelpExtraDataMap[key];
            const value = extra[key];

            result = { ...result, [backendKey]: value };
        });

        return result;
    };

    return (
        <Modal
            title={title}
            open={isModalOpen}
            onOk={handleOk}
            onCancel={handleCancel}
            cancelText={successfulSubmission ? "Close" : "Cancel"}
            okText="Send"
            okButtonProps={{ disabled: isSubmitting || successfulSubmission }}
        >
            <VStack gap={2} width="100%">
                {children && children}
                <Box width="100%">
                    <Input disabled value={subject} />
                </Box>
                <Box width="100%">
                    <Textarea
                        disabled={successfulSubmission}
                        minHeight={240}
                        value={supportMessage}
                        onChange={(e) => {
                            setSupportMessage(e.target.value);
                        }}
                        placeholder={`How can we help you?\n\nPlease describe your issue in detail. For example:\n• What were you trying to do?\n• What happened instead?\n• When did this start happening?\n• Are there any error messages?`}
                    />
                </Box>
                {successfulSubmission && (
                    <Box>
                        <FontAwesomeIcon icon={faCheckCircle} /> Successfully
                        submitted support request!
                    </Box>
                )}
                {submissionError && (
                    <Box>Support Request failed, try again</Box>
                )}
            </VStack>
        </Modal>
    );
};

export default HelpModal;
